/**
 * 星球总览（/world 访客页的 3D 球，CR-PUBLIC-WORLD-MAP-01-A）的纯计算：聚合、经纬度换球面、视野、拾取、放大交接。
 * 不引 three，可单测；globeStage 画球时用同一套换算，所以点在哪画、就在哪点得到。
 * - 坐标原样用公开图层给的位置。后端已按 grid_deg 吸附，同一格的坐标完全相同，这里只按坐标相等聚合，
 *   不按距离并、不为好看挪坐标；position 为空的不上球（人数由页面另报）。
 *   远看时几格的光点在屏幕上会互相盖住：只在画面上合成一个（groupOnScreen，人数相加、画在人最多那格的真实坐标上），
 *   点它就飞过去放大到交接阈值，由页面接着放大；拉近分得开后各自显示。
 * - 放大交接按屏幕短边方向看得到的弧（visibleSpanDeg）比阈值，和横竖屏无关。
 * - 坐标系不在这里换：球面上几百米的差看不出来，coord_system 原样带进 GlobeRegion，由页面接高德时再换。
 */
import type { PublicMapResident } from "@/shared/contracts/generated";

export interface GlobeCluster {
  /** 坐标相等的那一格：`lat,lng` 各保留 6 位小数（与 world_map/homeCluster 的 homeKey 同口径） */
  key: string;
  lat: number;
  lng: number;
  /** 这一格的居民，按 pet_id 排 */
  residents: PublicMapResident[];
}

/** 镜头：屏幕正中对着的经纬度，与镜头到球心的距离（球半径 1） */
export interface GlobeView {
  lat: number;
  lng: number;
  distance: number;
}

/** 放大到阈值时交给页面的地区：屏幕正中与大约看得到的范围（度） */
export interface GlobeRegion {
  center: { lat: number; lng: number };
  latSpan: number;
  lngSpan: number;
  coordSystem: string;
}

export interface ScreenPoint {
  key: string;
  x: number;
  y: number;
  /** 在朝向镜头的这半球上（背面的不画、也点不到） */
  front: boolean;
}

/** 屏幕短边方向的视角（度）：竖屏时按宽度算，球不会被左右裁掉 */
export const GLOBE_FOV_DEG = 40;
export const MIN_DISTANCE = 1.12;
export const MAX_DISTANCE = 5;
/** 开场：球的直径约占短边的八成 */
export const DEFAULT_DISTANCE = 3.6;
export const MAX_VIEW_LAT = 80;
/** 短边方向看得到的弧小于这么多度时，把地区交给页面 */
export const DEFAULT_REGION_SPAN_DEG = 25;
/** 交过一次地区以后，拉远到阈值的这么多倍才重新准备（同一次手势里来回抖不会重复交） */
export const REARM_FACTOR = 1.15;
/** 光点离球面的高度（球半径 1）：画和拾取用同一个 */
export const POINT_RADIUS = 1.012;
/** 光点直径（CSS 像素）：一位居民最小，人越多越大，封顶 */
export const POINT_MIN_PX = 34;
export const POINT_MAX_PX = 52;
/** 点得到的半径至少 22px（直径 44） */
export const HIT_MIN_RADIUS_PX = 22;
/** 世界页总览的居民灯直径（CSS 像素）：一位 20（单个居民也看得见），人越多越大（开方），封顶 44（UI-REVIEW110-03） */
export const LIGHT_MIN_PX = 20;
export const LIGHT_MAX_PX = 44;
/** 一位带位置的居民都没有时的开场朝向（只是镜头朝哪，不是谁的位置） */
export const FALLBACK_CENTER = { lat: 28, lng: 110 } as const;

/** 球面配色（sRGB）：夜色用深蓝、靛紫，不用深绿；光点用暖色，人数数字用深靛，对比度见用例 */
export const GLOBE_COLORS = {
  oceanDeep: "#0a1233",
  oceanLit: "#1f2a6e",
  rim: "#7a6cf0",
  graticule: "#9aa4ff",
  atmosphere: "#6d78ff",
  point: "#ffd27a",
  pointEdge: "#fff3d6",
  pointGlow: "#ffb84d",
  digit: "#1b1d48",
  selectedRing: "#ffffff",
  selectedHalo: "#a99bff",
  /** 状态环三段：在家、出门／到访、途中 */
  statusHome: "#ffe1a3",
  statusOut: "#ff8fa8",
  statusTransit: "#7cc8ff",
  /** 途中示意弧线、事件闪光环 */
  route: "#8fd0ff",
  flash: "#fff1c9",
  /** 宇宙开场：夜空底色、冷白星、暖黄星 */
  space: "#1b1a2b",
  starCool: "#d6ddff",
  starWarm: "#ffe1a3",
} as const;

/** 页面传来的一条「刚发生了事」：同一个 key 只闪一次；at 是毫秒时间戳 */
export interface GlobeHighlight {
  key: string;
  lat: number;
  lng: number;
  at: number;
}

/** 页面传来的途中示意：从哪一带到哪一带（片区近似） */
export interface GlobeRoute {
  key: string;
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
}

/** 光点状态环的三类；unknown、not_activated 只计人数、不上环 */
export type StatusBucket = "home" | "out" | "transit";
export interface StatusMix {
  home: number;
  out: number;
  transit: number;
}

export function statusBucket(presence: PublicMapResident["presence"]): StatusBucket | null {
  if (presence === "at_home") return "home";
  if (presence === "visiting" || presence === "at_destination") return "out";
  if (presence === "in_transit" || presence === "returning") return "transit";
  return null;
}

export function statusMix(residents: readonly PublicMapResident[]): StatusMix {
  const mix: StatusMix = { home: 0, out: 0, transit: 0 };
  for (const r of residents) {
    const b = statusBucket(r.presence);
    if (b) mix[b]++;
  }
  return mix;
}

export function addMix(a: StatusMix, b: StatusMix): StatusMix {
  return { home: a.home + b.home, out: a.out + b.out, transit: a.transit + b.transit };
}

/** 事件闪光：页面传来的 at（毫秒时间戳）离现在超过这么久的不闪（刚挂载时页面带来的旧事件不一起闪） */
export const FLASH_FRESH_MS = 10_000;
export const FLASH_MS = 1200;
export const REDUCED_BRIGHT_MS = 3000;

/** 这一条该不该闪：同一个 key 只处理一次；太旧的不闪 */
export function shouldFlash(item: { key: string; at: number }, seen: ReadonlySet<string>, now: number): boolean {
  return !seen.has(item.key) && Number.isFinite(item.at) && now - item.at <= FLASH_FRESH_MS;
}

/**
 * 途中示意弧线的点：沿大圆从 from 到 to，中间拱起 lift（球半径 1），两端贴着球面。只是示意，不是路线。
 * 返回 segments+1 个球面坐标。
 */
export function arcPoints(from: { lat: number; lng: number }, to: { lat: number; lng: number }, segments = 32, lift = 0.06): [number, number, number][] {
  const a = latLngToVector(from.lat, from.lng);
  const b = latLngToVector(to.lat, to.lng);
  const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const omega = Math.acos(dot);
  const out: [number, number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    let p: [number, number, number];
    if (omega < 1e-6) p = [...a];
    else {
      const s = Math.sin(omega);
      const wa = Math.sin((1 - t) * omega) / s;
      const wb = Math.sin(t * omega) / s;
      p = [a[0] * wa + b[0] * wb, a[1] * wa + b[1] * wb, a[2] * wa + b[2] * wb];
    }
    const r = 1.003 + lift * Math.sin(Math.PI * t) * Math.min(1, omega / (20 * RAD));
    const len = Math.hypot(p[0], p[1], p[2]) || 1;
    out.push([(p[0] / len) * r, (p[1] / len) * r, (p[2] / len) * r]);
  }
  return out;
}

const RAD = Math.PI / 180;

export function clusterKey(lat: number, lng: number): string {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}

function validPosition(r: PublicMapResident): r is PublicMapResident & { position: NonNullable<PublicMapResident["position"]> } {
  const p = r.position;
  return Boolean(p) && Number.isFinite(p!.lat) && Number.isFinite(p!.lng) && Math.abs(p!.lat) <= 90 && Math.abs(p!.lng) <= 180;
}

/** 按坐标相等聚成光点；人多的在前（同数按 key），同一 pet_id 只算一次。坐标不改。 */
export function clusterResidents(residents: readonly PublicMapResident[]): GlobeCluster[] {
  const groups = new Map<string, GlobeCluster>();
  const seen = new Set<string>();
  for (const r of residents) {
    if (!validPosition(r) || seen.has(r.pet_id)) continue;
    seen.add(r.pet_id);
    const key = clusterKey(r.position.lat, r.position.lng);
    let g = groups.get(key);
    if (!g) {
      g = { key, lat: r.position.lat, lng: r.position.lng, residents: [] };
      groups.set(key, g);
    }
    g.residents.push(r);
  }
  const order = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  for (const g of groups.values()) g.residents.sort((a, b) => order(a.pet_id, b.pet_id));
  return [...groups.values()].sort((a, b) => b.residents.length - a.residents.length || order(a.key, b.key));
}

/** 没上球的人数（position 为空或不合法）；页面拿它说「暂时不知道在哪」 */
export function unplacedCount(residents: readonly PublicMapResident[]): number {
  const seen = new Set<string>();
  let n = 0;
  for (const r of residents) {
    if (seen.has(r.pet_id)) continue;
    seen.add(r.pet_id);
    if (!validPosition(r)) n++;
  }
  return n;
}

/** 经纬度 → 球面坐标；与 three 的 SphereGeometry 贴图一致（2:1 等距柱状投影，左边缘是经度 -180） */
export function latLngToVector(lat: number, lng: number, radius = 1): [number, number, number] {
  const phi = (lng + 180) * RAD;
  const theta = (90 - lat) * RAD;
  return [-radius * Math.sin(theta) * Math.cos(phi), radius * Math.cos(theta), radius * Math.sin(theta) * Math.sin(phi)];
}

/** 让 view 的经纬度转到正对镜头（+Z）所需的球体转角，欧拉顺序 XYZ（先绕 Y 再绕 X） */
export function viewRotation(view: Pick<GlobeView, "lat" | "lng">): { x: number; y: number } {
  return { x: view.lat * RAD, y: -(view.lng + 90) * RAD };
}

/** 把球面上的点按 viewRotation 转过去（与 three 的 Euler XYZ 同一个矩阵：v' = Rx · Ry · v） */
export function rotateToView(v: readonly [number, number, number], view: Pick<GlobeView, "lat" | "lng">): [number, number, number] {
  const { x: ax, y: ay } = viewRotation(view);
  const [x, y, z] = v;
  const x1 = x * Math.cos(ay) + z * Math.sin(ay);
  const z1 = -x * Math.sin(ay) + z * Math.cos(ay);
  const y2 = y * Math.cos(ax) - z1 * Math.sin(ax);
  const z2 = y * Math.sin(ax) + z1 * Math.cos(ax);
  return [x1, y2, z2];
}

/** 经度归到 [-180, 180)；本来就在范围里的原样返回（取模会带进浮点误差，交给页面的坐标要和居民坐标一模一样） */
export function normalizeLng(lng: number): number {
  if (lng >= -180 && lng < 180) return lng;
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

export function clampDistance(d: number): number {
  return Math.min(MAX_DISTANCE, Math.max(MIN_DISTANCE, d));
}

export function clampView(view: GlobeView): GlobeView {
  return {
    lat: Math.min(MAX_VIEW_LAT, Math.max(-MAX_VIEW_LAT, view.lat)),
    lng: normalizeLng(view.lng),
    distance: clampDistance(view.distance),
  };
}

/** 视角半角 halfFovDeg 下，从距离 distance 看出去，球面上看得到的弧（度）：边缘射线打到球面就按交点算，打不到就按地平线算 */
export function visibleArcDeg(distance: number, halfFovDeg: number): number {
  const h = halfFovDeg * RAD;
  if (Math.sin(h) >= 1 / distance) return (2 * Math.acos(1 / distance)) / RAD;
  const t = distance * Math.cos(h) - Math.sqrt(1 - distance * distance * Math.sin(h) * Math.sin(h));
  return (2 * Math.atan2(t * Math.sin(h), distance - t * Math.cos(h))) / RAD;
}

/** 镜头的竖直视角（three 的 PerspectiveCamera.fov）：横屏就是 GLOBE_FOV_DEG，竖屏放大到让水平方向是 GLOBE_FOV_DEG */
export function verticalFovDeg(aspect: number): number {
  if (aspect >= 1) return GLOBE_FOV_DEG;
  return (2 * Math.atan(Math.tan((GLOBE_FOV_DEG / 2) * RAD) / Math.max(aspect, 0.1))) / RAD;
}

/** 屏幕短边方向看得到的弧（度）：放大交接的阈值按它比，和屏幕横竖无关 */
export function visibleSpanDeg(distance: number): number {
  return visibleArcDeg(distance, GLOBE_FOV_DEG / 2);
}

/** 短边方向看得到 spanDeg 度时镜头的距离（二分；夹在远近之间） */
export function distanceForSpan(spanDeg: number): number {
  let lo = MIN_DISTANCE;
  let hi = MAX_DISTANCE;
  if (visibleSpanDeg(lo) >= spanDeg) return lo;
  if (visibleSpanDeg(hi) <= spanDeg) return hi;
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    if (visibleSpanDeg(mid) < spanDeg) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** 当前镜头对应的地区：正中与大约范围（纬度按竖直视角、经度按水平视角并按纬度展宽，封顶 360） */
export function regionFor(view: GlobeView, aspect: number, coordSystem: string): GlobeRegion {
  const vfov = verticalFovDeg(aspect);
  const halfH = Math.atan(Math.tan((vfov / 2) * RAD) * Math.max(aspect, 0.1)) / RAD;
  const latSpan = visibleArcDeg(view.distance, vfov / 2);
  const lngSpan = Math.min(360, visibleArcDeg(view.distance, halfH) / Math.max(Math.cos(view.lat * RAD), 0.2));
  return { center: { lat: view.lat, lng: normalizeLng(view.lng) }, latSpan, lngSpan, coordSystem };
}

/** 放大交接的开关：准备好时跨过阈值就交一次并收起；拉远到 REARM_FACTOR 倍以上才重新准备 */
export function regionStep(armed: boolean, spanDeg: number, thresholdDeg: number): { fire: boolean; armed: boolean } {
  if (armed && spanDeg < thresholdDeg) return { fire: true, armed: false };
  if (!armed && spanDeg > thresholdDeg * REARM_FACTOR) return { fire: false, armed: true };
  return { fire: false, armed };
}

/** 开场：朝向居民最多的那一格（同数取 key 小的）；没有就朝 FALLBACK_CENTER */
export function initialView(clusters: readonly GlobeCluster[]): GlobeView {
  const top = clusters[0];
  return clampView({ lat: top ? top.lat : FALLBACK_CENTER.lat, lng: top ? top.lng : FALLBACK_CENTER.lng, distance: DEFAULT_DISTANCE });
}

/** 球面上一点在屏幕上的位置（CSS 像素），与 globeStage 的透视镜头同一套：镜头在 (0,0,distance) 看向球心，竖直视角 verticalFovDeg */
export function projectToScreen(lat: number, lng: number, view: GlobeView, width: number, height: number, radius = POINT_RADIUS): { x: number; y: number; front: boolean } {
  const [x, y, z] = rotateToView(latLngToVector(lat, lng, radius), view);
  const depth = view.distance - z;
  const aspect = width / Math.max(height, 1);
  const t = Math.tan((verticalFovDeg(aspect) / 2) * RAD);
  const ndcX = x / depth / (t * aspect);
  const ndcY = y / depth / t;
  // 朝向镜头：从镜头看过去这一点没被球挡住（点在镜头一侧的切平面之外）
  const front = z * view.distance > radius * radius && depth > 0;
  return { x: ((ndcX + 1) / 2) * width, y: ((1 - ndcY) / 2) * height, front };
}

/** 光点直径（CSS 像素）：一位 34，按人数对数长大，封顶 52 */
export function pointSizePx(count: number): number {
  return Math.min(POINT_MAX_PX, POINT_MIN_PX + 6 * Math.log2(Math.max(1, count)));
}

/** 世界页总览的居民灯直径（CSS 像素）：一位 LIGHT_MIN_PX，按人数开方长大，封顶 LIGHT_MAX_PX（UI-REVIEW110-03） */
export function lightSizePx(count: number): number {
  return Math.min(LIGHT_MAX_PX, LIGHT_MIN_PX + 4 * Math.sqrt(Math.max(0, count - 1)));
}

/** 居民灯亮芯到哪儿（径向渐变的位置，0～0.5 是灯的半径内）：按亮度档 0～3 */
export const LIGHT_CORE_STOPS = [0.26, 0.32, 0.38, 0.44] as const;
/**
 * 世界页总览居民灯的亮度（UI-WORLD-FOLLOWUP-01；I 10-14「聚在一起时可以亮一点、大一点」，大一点由 lightSizePx 管）：
 * 一位是 0 档（亮芯到 0.26、不透明度 0.9，和原来一样），2～4 位 1 档、5 位起 2 档，事件提亮（减少动态下的提示）再加一档，封顶 3 档；
 * 0 档以上不透明度都是满的，外晕跟着亮芯往外挪。只调亮芯范围和不透明度，颜色仍是家灯两色。
 */
export function lightStrength(count: number, bright: boolean): { level: number; core: number; glow: number; alpha: number } {
  const level = Math.min(3, (count >= 5 ? 2 : count >= 2 ? 1 : 0) + (bright ? 1 : 0));
  const core = LIGHT_CORE_STOPS[level];
  return { level, core, glow: core + 0.26, alpha: level > 0 ? 1 : 0.9 };
}

/** 世界页总览什么时候写人数：选中的那组，或放大到看得到的弧不超过交接阈值两倍（快到城市尺度）；平时只画灯（UI-REVIEW110-03） */
export function showsCount(spanDeg: number, regionSpanDeg: number, selected: boolean): boolean {
  return selected || spanDeg <= regionSpanDeg * 2;
}

/** 居民灯呼吸：约 3 秒一个来回，大小 ±8%；减少动态下恒为 1（不动）（UI-REVIEW110-03） */
export function breathScale(ambientMs: number, reduced: boolean): number {
  return reduced ? 1 : 1 + 0.08 * Math.sin((ambientMs / 3000) * Math.PI * 2);
}

/**
 * 每团灯自己的呼吸相位（毫秒，0～2999；WORLD-RESIDENTS-ALIVE-117）：按这一团的代表 key 算（FNV-1a），同一团永远同一个相位，
 * 几团灯不再齐步呼吸，看着像各自活着。只是相位，不改大小幅度与周期。
 */
export function breathPhaseMs(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 3000;
}

/** 一团里的人是不是都在驿站睡着了（asleep 为 true，B-PUBLIC-RESIDENT-ASLEEP-116）：有一位醒着、在外、说不清，或这一团没人，都不算 */
export function allAsleep(residents: readonly Pick<PublicMapResident, "presence" | "asleep">[]): boolean {
  return residents.length > 0 && residents.every((r) => r.presence === "at_home" && r.asleep === true);
}

/** 都睡着那团灯的不透明度系数（暗一档）；呼吸照旧有、慢一倍（减少动态下照旧不动） */
export const ASLEEP_LIGHT_ALPHA = 0.55;

/** 路线小光走完一条示意弧线要多久（毫秒）；只示意从出发那头往到达那头的方向，不代表走到了哪 */
export const ROUTE_COMET_MS = 2400;

/** 路线小光此刻在弧线上的位置（0～1）：按世界页的动画时钟走，各条路线错开相位 */
export function cometT(ambientMs: number, key: string): number {
  const ms = (((ambientMs + breathPhaseMs(key)) % ROUTE_COMET_MS) + ROUTE_COMET_MS) % ROUTE_COMET_MS;
  return ms / ROUTE_COMET_MS;
}

/** 折线上按 0～1 取一点（按段数均分，arcPoints 本来就是均分的） */
export function pointOnArc(points: readonly (readonly [number, number, number])[], t: number): [number, number, number] {
  if (!points.length) return [0, 0, 0];
  if (points.length === 1) return [...points[0]] as [number, number, number];
  const x = Math.min(1, Math.max(0, t)) * (points.length - 1);
  const i = Math.min(points.length - 2, Math.floor(x));
  const k = x - i;
  const a = points[i];
  const b = points[i + 1];
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}

/** 光点上的人数：超过 999 写 999+ */
export function countLabel(count: number): string {
  return count > 999 ? "999+" : String(count);
}

/** 点在 (px,py) 上命中哪一个：正面的、在命中半径内的最近一个；都不中返回 null */
export function pickCluster(points: readonly (ScreenPoint & { radius?: number })[], px: number, py: number): string | null {
  let best: string | null = null;
  let bestD = Infinity;
  for (const p of points) {
    if (!p.front) continue;
    const r = Math.max(HIT_MIN_RADIUS_PX, p.radius ?? 0);
    const d = Math.hypot(p.x - px, p.y - py);
    if (d <= r && d < bestD) {
      best = p.key;
      bestD = d;
    }
  }
  return best;
}

/** 屏幕上的一个光点：可能是一格，也可能是远看时挤在一起的几格 */
export interface ScreenGroup extends ScreenPoint {
  /** 代表这一组的那一格（人最多的；同数取 key 小的）：画在它的真实坐标上 */
  key: string;
  /** 组里各格的 key，代表那格在前 */
  keys: string[];
  count: number;
  radius: number;
}

/**
 * 远看时挨得太近、光点会互相盖住的几格，只在画面上合成一个光点：人数相加，画在人最多那一格的真实坐标上（不取平均、不挪坐标）。
 * 拉近以后分得开就各自显示。只合正面的；背面的不画。数据上的聚合仍是坐标相等那一层（clusterResidents）。
 */
export function groupOnScreen(points: readonly (ScreenPoint & { count: number })[]): ScreenGroup[] {
  const front = points.filter((p) => p.front);
  const parent = front.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const r = front.map((p) => pointSizePx(p.count) / 2);
  for (let i = 0; i < front.length; i++) {
    for (let j = i + 1; j < front.length; j++) {
      if (Math.hypot(front[i].x - front[j].x, front[i].y - front[j].y) < r[i] + r[j]) parent[find(i)] = find(j);
    }
  }
  const sets = new Map<number, number[]>();
  front.forEach((_, i) => {
    const root = find(i);
    sets.set(root, [...(sets.get(root) ?? []), i]);
  });
  const order = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  return [...sets.values()].map((idx) => {
    const members = idx.map((i) => front[i]).sort((a, b) => b.count - a.count || order(a.key, b.key));
    const rep = members[0];
    const count = members.reduce((s, m) => s + m.count, 0);
    return { key: rep.key, keys: members.map((m) => m.key), count, x: rep.x, y: rep.y, front: true, radius: pointSizePx(count) / 2 };
  });
}

/**
 * 点了合成的光点以后镜头去哪：对准组里各格的中间（镜头中心不是谁的位置），从当前距离往近处找，
 * 找到第一个让这几格在屏幕上分开的距离就停在那（split）；一直到交接阈值还分不开，就停在阈值以内，交给页面（handoff）。
 */
export function focusTarget(
  members: readonly { key: string; lat: number; lng: number; count: number }[],
  from: GlobeView,
  width: number,
  height: number,
  regionSpanDeg: number,
  spanRatio = 0.9,
): { view: GlobeView; handoff: boolean } {
  const base = members[0];
  const lngs = members.map((m) => base.lng + normalizeLng(m.lng - base.lng)); // 跨 180° 经线时就近展开
  const center = { lat: members.reduce((s, m) => s + m.lat, 0) / members.length, lng: normalizeLng(lngs.reduce((s, x) => s + x, 0) / lngs.length) };
  // 只在阈值以外找「分得开」的距离：到了阈值以内就该交给页面了
  const splitStop = distanceForSpan(regionSpanDeg);
  const total = members.reduce((s, m) => s + m.count, 0);
  const steps = 32;
  if (from.distance > splitStop) {
    for (let i = 1; i <= steps; i++) {
      const d = 1 + (from.distance - 1) * Math.pow((splitStop - 1) / (from.distance - 1), i / steps);
      const view = clampView({ ...center, distance: d });
      const split = groupOnScreen(members.map((m) => ({ key: m.key, count: m.count, ...projectToScreen(m.lat, m.lng, view, width, height) })));
      // 分成了两个以上，而且每一格都在正面（没有转到背面去）
      if (split.length > 1 && split.reduce((s, g) => s + g.count, 0) === total) return { view, handoff: false };
    }
  }
  return { view: clampView({ ...center, distance: Math.min(from.distance, distanceForSpan(regionSpanDeg * spanRatio)) }), handoff: true };
}

/** 拖动时每像素转多少度（按短边）：按看得到的弧和球在屏幕上的直径取较小的那个分母（远看时整只球拖过一个直径约转 180°） */
export function degPerPixel(distance: number, shortSidePx: number): number {
  const t = Math.tan((GLOBE_FOV_DEG / 2) * RAD);
  const sphereDiameterPx = (Math.tan(Math.asin(1 / distance)) / t) * shortSidePx;
  return visibleSpanDeg(distance) / Math.max(1, Math.min(shortSidePx, sphereDiameterPx));
}

/** 缩放按离球面的高度乘：越近越细 */
export function zoomDistance(distance: number, factor: number): number {
  return clampDistance(1 + (distance - 1) * factor);
}

// ---- 宇宙开场（OBS-SPACE-01）：先在远处看见这颗星球，点它，穿过星空落到总览 ----
// 下面全是纯计算（不引 three）：星点的位置、视差、穿梭时间线、远景星球的点击范围。globeStage 按它画，用例按它核。

/** 远景：镜头离球心这么远，竖屏时球的直径约占短边三成，四周是星空 */
export const SPACE_DISTANCE = 9;
/** 穿梭一共多久（毫秒）：先加速、再减速，最后一帧就是总览。第一次进来用它 */
export const WARP_MS = 1900;
/** 短版穿梭（毫秒）：同一次打开里再进来时页面可以传它（2531 评审：每次 1.9 秒，第三次就嫌烦） */
export const WARP_SHORT_MS = 700;
/** 页面传的穿梭时长夹在这个范围里；不给、不是数就用 WARP_MS */
export const WARP_MIN_MS = 400;
export const WARP_MAX_MS = 4000;
/** 穿过去那一下的径向闪光：以穿梭正中为峰，标准差这么多毫秒（肉眼看得到的约 130ms） */
export const WARP_FLASH_SIGMA_MS = 45;
/** 穿梭途中视角最多再张开这么多度（中段最大，两端是 0）：给一点冲过去的感觉；张得太大会把正在变大的星球又缩回去（14° 时前半段几乎不长） */
export const WARP_FOV_KICK_DEG = 6;
/** 穿梭途中星点一共往镜头这边走多远（星点循环摆放，走过头的从远处补回来） */
export const WARP_TRAVEL = 150;
/** 远景时星球自己慢慢转（度每秒）；减少动态时不转 */
export const SPACE_SPIN_DEG_PER_S = 3;
/** 到了总览以后星空暗一点，别抢光点 */
export const LANDED_STAR_ALPHA = 0.7;
/** 远景星球的点击范围：比球面再宽出这么多像素，最小半径 22（直径 44） */
export const SPACE_HIT_SLOP_PX = 16;

/** 星点摆放的深度范围（镜头前方的距离，球半径 1）：最近的也在远景星球背面之后，所以永远被球挡在后面 */
export const STAR_NEAR = 14;
export const STAR_FAR = 60;
/** 星点循环时淡入淡出的边（深度），实际摆放留在这两条边以内 */
export const STAR_FADE = 4;
/**
 * 三层星空：远层多而小、拖动时移得少，近层少而大、移得多（视差）。
 * shift 是整数：经度转一整圈，每层正好挪过整数个画面宽，回到原位，不会跳。
 */
export const STAR_LAYERS = [
  { count: 360, near: 42, far: 56, shift: 1, size: 0.8 },
  { count: 220, near: 28, far: 42, shift: 2, size: 1.1 },
  { count: 110, near: 18, far: 28, shift: 3, size: 1.5 },
] as const;
export const STAR_SEED = 20261008;
/** 穿梭时拉线的星：近层全拉、粗一点；中层只拉三成、1px；远层保持点状（2531：几百根一样的细线像下雨，要少一半以上） */
export const STREAK_NEAR_PX = 1.8;
export const STREAK_MID_PX = 1;

export interface SpaceStar {
  /** 画面横向位置，-1 到 1（乘上那个深度的半宽才是实际位置） */
  u: number;
  /** 画面竖向位置，-1 到 1 */
  v: number;
  depth: number;
  /** 视差倍数：该层的 shift */
  shift: number;
  /** 亮点大小倍数 */
  size: number;
  /** 闪烁相位 0 到 1 */
  phase: number;
  /** 0 偏冷白，1 偏暖黄 */
  warmth: number;
}

/** 固定种子的伪随机数（mulberry32）：同一个种子每次都是同一片星空 */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 摆出整片星空：按层、按种子，结果固定 */
export function spaceStars(seed = STAR_SEED): SpaceStar[] {
  const rand = seededRandom(seed);
  const out: SpaceStar[] = [];
  for (const layer of STAR_LAYERS) {
    for (let i = 0; i < layer.count; i++) {
      const u = rand() * 2 - 1;
      const v = rand() * 2 - 1;
      const depth = layer.near + rand() * (layer.far - layer.near);
      // 大多数是小星，偶尔一颗亮的
      const r = rand();
      const size = layer.size * (r > 0.97 ? 2.1 : r > 0.85 ? 1.45 : 0.75 + r * 0.4);
      out.push({ u, v, depth, shift: layer.shift, size, phase: rand(), warmth: rand() < 0.22 ? 0.6 + rand() * 0.4 : rand() * 0.25 });
    }
  }
  return out;
}

/** 这颗星穿梭时拉不拉线、多粗（CSS 像素）：近层（shift 3）全拉，中层（shift 2）按闪烁相位只拉三成，远层 0＝不拉 */
export function streakWidthPx(star: Pick<SpaceStar, "shift" | "phase">): number {
  if (star.shift >= 3) return STREAK_NEAR_PX;
  if (star.shift === 2 && star.phase < 0.3) return STREAK_MID_PX;
  return 0;
}

/**
 * 星空跟着镜头挪多少（以半个画面为 1 的单位，乘上各层的 shift）：拖着星球往东转，星空往西挪。
 * 经度一整圈正好是 2（一个画面宽），所以跨过 ±180° 时不跳。
 */
export function starShift(view: Pick<GlobeView, "lat" | "lng">): { x: number; y: number } {
  return { x: -view.lng / 180, y: view.lat / 180 };
}

/** 把挪过的位置折回 -1 到 1（星点在画面上循环摆放） */
export function wrapUnit(x: number): number {
  return ((((x + 1) % 2) + 2) % 2) - 1;
}

/** 穿梭途中某一刻：镜头、视角、星点拉成线的程度、两张贴图的混合、光点与星空的透明度 */
export interface WarpFrame {
  /** 镜头（距离可以超过 MAX_DISTANCE：穿梭从远景开始） */
  view: GlobeView;
  fovKickDeg: number;
  /** 星点拉成线的程度 0 到 1 */
  warp: number;
  /** 星点已经往镜头这边走了多远 */
  travel: number;
  /** 背光那一侧用不用远景夜色贴图（1 用；0 只把总览贴图压暗）。受光那一侧始终是总览贴图 */
  farMix: number;
  /** 光点、经纬网、途中弧线的透明度 */
  dotsAlpha: number;
  starAlpha: number;
  /** 太阳方向（镜头坐标，单位向量）：远景从侧面照、看得到明暗交界，穿梭中段转到正前偏左，亮面扫过整颗星球 */
  sun: [number, number, number];
  /** 穿过去那一下的径向闪光 0 到 1 */
  flash: number;
}

const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** 先慢后快再慢（三次） */
export function easeInOutCubic(k: number): number {
  const t = Math.min(1, Math.max(0, k));
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** 页面给的穿梭时长：不给、不是数就用 WARP_MS，其余夹在 WARP_MIN_MS 到 WARP_MAX_MS */
export function warpDurationMs(ms?: number | null): number {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return WARP_MS;
  return Math.min(WARP_MAX_MS, Math.max(WARP_MIN_MS, ms));
}

/** 远景时的太阳（镜头坐标）：从左侧稍偏前照过来，右边三成左右是背光面，看得到明暗交界 */
export const SUN_SPACE: readonly [number, number, number] = unit3([-0.88, 0.28, 0.38]);
/** 落地后的太阳：正前偏左上，整面都亮，只有右侧边缘略暗（白天贴图也有一侧受光，和远景同一个方向感） */
export const SUN_LANDED: readonly [number, number, number] = unit3([-0.42, 0.36, 0.83]);
/** 明暗交界的柔和带：太阳方向点积在这个区间内从全暗过渡到全亮（着色器和静态星球图用同一组数） */
export const TERMINATOR: readonly [number, number] = [-0.12, 0.38];

function unit3(v: readonly [number, number, number]): [number, number, number] {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

/** 穿梭到 k 时的太阳方向：三成之前还是远景的侧光，九成时已经转到落地的正前偏左（中间平滑转过去） */
export function spaceSun(k: number): [number, number, number] {
  const t = smooth(0.3, 0.9, k);
  return unit3([SUN_SPACE[0] + (SUN_LANDED[0] - SUN_SPACE[0]) * t, SUN_SPACE[1] + (SUN_LANDED[1] - SUN_SPACE[1]) * t, SUN_SPACE[2] + (SUN_LANDED[2] - SUN_SPACE[2]) * t]);
}

/** 穿过去那一下的闪光：穿梭正中为峰（1），按毫秒算宽度，所以短版穿梭的闪光也是一样长的一下 */
export function warpFlash(k: number, durationMs = WARP_MS): number {
  if (!(k > 0) || k >= 1) return 0;
  const ms = (k - 0.5) * durationMs;
  return Math.exp(-0.5 * (ms / WARP_FLASH_SIGMA_MS) ** 2);
}

/**
 * 穿梭时间线：k 从 0（还在远景）到 1（落到总览）。
 * - 镜头从 SPACE_DISTANCE 推到 to.distance：前段就明显长大（和星线最长那段同时到），落地前减速；经纬度沿近的那一边转到 to；
 * - 视角中段张开、两端收回；星点中段拉成线最长；正中有一下径向闪光；
 * - 太阳从侧面转到正前偏左，亮面扫过整颗星球（受光处是总览贴图，背光处是夜色贴图）；夜色贴图在中后段退场；
 * - 光点、经纬网最后两成才淡入；k ≥ 1 时一分不差就是 to：落地那一帧就是总览。
 */
export function warpFrame(k: number, from: Pick<GlobeView, "lat" | "lng">, to: GlobeView, durationMs = WARP_MS): WarpFrame {
  if (!(k > 0)) {
    return { view: { lat: from.lat, lng: normalizeLng(from.lng), distance: SPACE_DISTANCE }, fovKickDeg: 0, warp: 0, travel: 0, farMix: 1, dotsAlpha: 0, starAlpha: 1, sun: [...SUN_SPACE], flash: 0 };
  }
  if (k >= 1) return { view: { ...to }, fovKickDeg: 0, warp: 0, travel: WARP_TRAVEL, farMix: 0, dotsAlpha: 1, starAlpha: LANDED_STAR_ALPHA, sun: [...SUN_LANDED], flash: 0 };
  const e = easeInOutCubic(k);
  // 按 1/距离 插值、先快后慢：星球在屏幕上的大小前段就明显长大（2531：星线最长时球不动像「光在冲、我没走」），落地前减速
  const grow = 1 - Math.pow(1 - k, 2.4);
  const dl = ((((to.lng - from.lng) % 360) + 540) % 360) - 180; // 走近的那一边
  const bump = Math.sin(Math.PI * k);
  return {
    view: {
      lat: from.lat + (to.lat - from.lat) * e,
      lng: normalizeLng(from.lng + dl * e),
      distance: 1 / (1 / SPACE_DISTANCE + (1 / to.distance - 1 / SPACE_DISTANCE) * grow),
    },
    fovKickDeg: WARP_FOV_KICK_DEG * bump * bump,
    warp: Math.pow(bump, 1.4),
    travel: WARP_TRAVEL * e,
    farMix: 1 - smooth(0.55, 0.95, k),
    dotsAlpha: smooth(0.8, 1, k),
    starAlpha: 1 - (1 - LANDED_STAR_ALPHA) * smooth(0.6, 1, k),
    sun: spaceSun(k),
    flash: warpFlash(k, durationMs),
  };
}

/** 远景星球在屏幕上的半径（CSS 像素）：镜头在 distance，竖直视角按屏幕比例 */
export function spaceEarthRadiusPx(width: number, height: number, distance = SPACE_DISTANCE): number {
  const aspect = width / Math.max(height, 1);
  const t = Math.tan((verticalFovDeg(aspect) / 2) * RAD);
  return (Math.tan(Math.asin(1 / Math.max(distance, 1.0001))) / t) * (height / 2);
}

/** 远景时点在 (x,y) 算不算点到了星球：球在画面正中，范围比球面宽 SPACE_HIT_SLOP_PX，最小半径 HIT_MIN_RADIUS_PX */
export function spaceEarthHit(x: number, y: number, width: number, height: number, distance = SPACE_DISTANCE): boolean {
  const r = Math.max(HIT_MIN_RADIUS_PX, spaceEarthRadiusPx(width, height, distance) + SPACE_HIT_SLOP_PX);
  return Math.hypot(x - width / 2, y - height / 2) <= r;
}
